import { IPromise } from 'angular';

import { BASE_PATH_API } from '@manager-base/app-base/environment/paths/constants';
import { createActions } from '@manager-base/app-base/config/data-access';

import { AssociationViolationCountsDTO } from '@manager-base/data/dtos/property-violations/association-violation-counts.dto';
import { CreateViolationStageDTO } from '@manager-base/data/dtos/property-violations/create-violation-stage.dto';
import { DeleteViolationStageDTO } from '@manager-base/data/dtos/property-violations/delete-violation-stage.dto';
import { EntityFileDTO } from '@root/src/shared/domain/dtos/entity-file.dto';
import { LookupDTO } from '@manager-base/data/dtos/lookup.dto';
import { PagingResultsDTO } from '@manager-base/data/dtos/paging-results.dto';
import { UpdateViolationStageDTO } from '@manager-base/data/dtos/property-violations/update-violation-stage.dto';
import { UpdateViolationViolationStageDTO } from '@manager-base/data/dtos/property-violations/update-violation-violation-stage.dto';
import { UpdateViolationViolationStageV2DTO } from '@manager-base/data/dtos/property-violations/update-violation-violation-stage-v2.dto';
import { ViolationActivityDTO } from '@manager-base/data/dtos/property-violations/violation-activity.dto';
import { ViolationCategoryDTO } from '@manager-base/data/dtos/violation-category.dto';
import { ViolationResponseDTO } from '@manager-base/data/dtos/property-violations/violation-response.dto';
import { ViolationStageDTO } from '@manager-base/data/dtos/property-violations/violation-stage.dto';
import { ViolationTypeDTO } from '@manager-base/data/dtos/violation-type.dto';
import { ViolationViolationStageDTO } from '@manager-base/data/dtos/property-violations/violation-violation-stage.dto';
import { ViolationDTO } from '@manager-base/data/dtos/property-violations/violation.dto';
import { ChargeDTO } from '@root/manager-base/data/dtos/account/charge.dto';
import { ViolationFineDTO } from '@root/manager-base/data/dtos/property-violations/violation-fine.dto';
import { ViolationPrefillDTO } from '@root/manager-base/data/dtos/property-violations/violation-prefill.dto';
import { ViolationMessageDTO } from '@root/manager-base/data/dtos/associations/violation-message.dto';
import { CreateViolationNoteDTO } from '@root/manager-base/data/dtos/property-violations/create-violation-note.dto';

const controllerPath = `${BASE_PATH_API}/properties`;

const actions = {
    getViolationCategories: {
        method: 'GET',
        url: `${controllerPath}/:propertyId/violationCategories`
    },
    createDefaultCategories: {
        method: 'POST',
        url: `${controllerPath}/:propertyId/violationCategories/defaults`
    },
    getViolationsPaged: {
        method: 'GET',
        url: `${BASE_PATH_API}/v2/violations`,
        params: {
            '$count': true
        }
    },
    saveViolation: {
        method: 'POST',
        url: `${BASE_PATH_API}/violations`
    },
    saveViolationCategory: {
        method: 'POST',
        url: `${BASE_PATH_API}/violationCategories`
    },
    updateViolationCategory: {
        method: 'PUT',
        url: `${BASE_PATH_API}/violationCategories/:violationCategoryId`
    },
    deleteViolationCategory: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/violationCategories/:violationCategoryId`
    },
    updateViolationType: {
        method: 'PUT',
        url: `${BASE_PATH_API}/violationTypes/:violationTypeId`
    },
    getViolation: {
        method: 'GET',
        url: `${BASE_PATH_API}/v2/violations/:violationId`
    },
    updateViolation: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v4/violations/:violationId`
    },
    deleteViolation: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/violations/:violationId`
    },
    getViolationFiles: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/:violationId/files`,
        isArray: true
    },
    saveViolationFiles: {
        method: 'POST',
        url: `${BASE_PATH_API}/violations/:violationId/files`,
        isArray: true
    },
    // TODO: saveViolationStage does not appear to be referenced from anywhere, as it has been replaced by saveViolationStageV2
    saveViolationStage: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v2/violations/:violationId/violationStage`
    },
    saveViolationStageV2: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v3/violations/:violationId/violationStage`
    },
    saveViolationStageBulk: {
        method: 'PUT',
        url: `${BASE_PATH_API}/v1/associations/:propertyId/violations/stage`
    },
    getViolationCounts: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/counts?numberOfAssociationsToReturn?dateDue`
    },
    deleteViolationType: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/violationTypes/:violationTypeId`
    },
    getViolationCountForCategory: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/counts/violationCategories/:violationCategoryId`
    },
    getViolationCountForViolationType: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/counts/violationTypes/:violationTypeId`
    },
    getViolationCountForViolationStage: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/counts/violationStages/:violationStageId`
    },
    getViolationActivity: {
        method: 'GET',
        url: `${BASE_PATH_API}/violations/:violationId/activity`
    },
    createNoteV2: {
        method: 'PUT',
        url: `${BASE_PATH_API}/violations/v2/:violationId/activity/notes`
    },
    getViolationStages: {
        method: 'GET',
        url: `${BASE_PATH_API}/violationStages`,
        params: {
            'propertyId': 'propertyId'
        }
    },
    getAllViolationStages: {
        method: 'GET',
        url: `${BASE_PATH_API}/violationStages`
    },
    getAllViolationStageMailingTemplateNamesCached: {
        method: 'GET',
        url: `${BASE_PATH_API}/violationStages/mailingTemplates`,
        cache: true,
    },
    addViolationStage: {
        method: 'POST',
        url: `${BASE_PATH_API}/violationStages`
    },
    updateViolationStageSetting: {
        method: 'PUT',
        url: `${BASE_PATH_API}/violationStages`
    },
    removeViolationStage: {
        method: 'DELETE',
        url: `${BASE_PATH_API}/violationStages`
    },
    saveFine: {
        isArray: true,
        method: 'POST',
        url: `${BASE_PATH_API}/v2/violations/fine`
    },
    getLastThreeViolationsToPrefill: {
        isArray: true,
        method: 'GET',
        url: `${BASE_PATH_API}/v2/violations/:propertyId/prefill`
    },
    sendMessageToOwner: {
        isArray: true,
        method: 'PUT',
        url: `${BASE_PATH_API}/violations/:violationId/activity/messagetoowner`
    },
};

export interface IViolationsApi {
    getViolationCategories(params: { propertyId: number }): IPromise<ViolationCategoryDTO[]>;
    createDefaultCategories(params: { propertyId: number }): IPromise<ViolationCategoryDTO[]>;
    getViolationsPaged(params: { '$count'?: boolean, includeCoverImages?: boolean, includeAttachments?: boolean, unitAgreementId?: number, onlyUnresolvedStages?: boolean }): IPromise<PagingResultsDTO<ViolationResponseDTO>>;
    saveViolation(dto: ViolationDTO): IPromise<ViolationResponseDTO>;
    saveViolationCategory(dto: ViolationCategoryDTO): IPromise<ViolationCategoryDTO[]>;
    updateViolationCategory(params: { violationCategoryId: number }, dto: ViolationCategoryDTO): IPromise<ViolationCategoryDTO>;
    deleteViolationCategory(params: { violationCategoryId: number }): IPromise<void>;
    updateViolationType(params: { violationTypeId: number }, dto: ViolationTypeDTO): IPromise<ViolationTypeDTO>;
    getViolation(params: { violationId: number }): IPromise<ViolationResponseDTO>;
    updateViolation(params: { violationId: number }, dto: ViolationDTO): IPromise<ViolationResponseDTO>;
    deleteViolation(params: { violationId: number }): IPromise<void>;
    getViolationFiles(params: { violationId: number }): IPromise<EntityFileDTO[]>;
    saveViolationFiles(params: { violationId: number }, dtos: EntityFileDTO[]): IPromise<EntityFileDTO[]>;
    saveViolationStage(params: { violationId: number }, dto: UpdateViolationViolationStageDTO): IPromise<ViolationViolationStageDTO>;
    saveViolationStageV2(params: { violationId: number }, dto: UpdateViolationViolationStageV2DTO): IPromise<ViolationViolationStageDTO>;
    saveViolationStageBulk(params: { propertyId: number }, dto: UpdateViolationViolationStageV2DTO): IPromise<void>;
    getViolationCounts(params: { numberOfAssociationsToReturn?: number, dateDue?: string }): IPromise<PagingResultsDTO<AssociationViolationCountsDTO>>;
    deleteViolationType(params: { violationTypeId: number }): IPromise<void>;
    getViolationCountForCategory(params: { violationCategoryId: number }): IPromise<number>;
    getViolationCountForViolationType(params: { violationTypeId: number }): IPromise<number>;
    getViolationCountForViolationStage(params: { violationStageId: number }): IPromise<number>;
    getViolationActivity(params: { violationId: number }): IPromise<ViolationActivityDTO[]>;
    createNoteV2(params: { violationId: number }, dto: CreateViolationNoteDTO): IPromise<ViolationActivityDTO>;
    getViolationStages(params: { propertyId: number }): IPromise<ViolationStageDTO[]>;
    getAllViolationStages(): IPromise<{ [index: number]: ViolationStageDTO[] }>;
    getAllViolationStageMailingTemplateNamesCached(): IPromise<{ [index: number]: LookupDTO[] }>;
    addViolationStage(dto: CreateViolationStageDTO): IPromise<ViolationStageDTO>;
    updateViolationStageSetting(dto: UpdateViolationStageDTO): IPromise<ViolationStageDTO>;
    removeViolationStage(dto: DeleteViolationStageDTO): IPromise<void>;
    saveFine(dto: ViolationFineDTO): IPromise<ChargeDTO>;
    getLastThreeViolationsToPrefill(params: { propertyId: number }): IPromise<ViolationPrefillDTO[]>;
    sendMessageToOwner(params: { violationId: number }, dto: ViolationMessageDTO): IPromise<boolean>;
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function ViolationsApi(): IViolationsApi {
    return createActions(actions);
}
